import React from 'react';
import styled from 'styled-components';
import { Card, Button } from '../../../styles/components';

const DetailsContainer = styled(Card)`
  padding: 2rem;
  margin-bottom: 0;
  max-height: 80vh;
  overflow-y: auto;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(99, 102, 241, 0.3);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  z-index: 101;
  border-radius: 16px;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.3);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.5);
  }
`;

const DetailsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.1),
      rgba(99, 102, 241, 0.5),
      rgba(99, 102, 241, 0.1)
    );
  }
`;

const Title = styled.h2`
  margin-bottom: 0.75rem;
  font-size: 1.75rem;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0.95),
    rgba(99, 102, 241, 0.9)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  margin-bottom: 1rem;
  opacity: 0.9;
`;

const DetailSection = styled.div`
  margin-bottom: 2.5rem;
`;

const SectionTitle = styled.h3`
  margin: 1.25rem 0;
  padding-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.25rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 30%;
    height: 2px;
    background: linear-gradient(
      to right,
      rgba(99, 102, 241, 0.8),
      rgba(99, 102, 241, 0.2)
    );
  }
`;

const DetailItem = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.h4`
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Value = styled.p`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const SpeakerCard = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.backgroundLight};
  border-radius: 8px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SpeakerImage = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const SpeakerInfo = styled.div`
  flex: 1;
`;

const SpeakerName = styled.h4`
  margin-bottom: 0.3rem;
`;

const SpeakerTitle = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const AgendaItem = styled.div`
  display: flex;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AgendaTime = styled.div`
  width: 150px;
  font-weight: 500;
  flex-shrink: 0;
`;

const AgendaActivity = styled.div`
  flex: 1;
`;

const MaterialLink = styled.a`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.5rem;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.3rem 0.8rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 0.85rem;
  background-color: ${({ status, theme }) => 
    status === 'upcoming' 
      ? theme.colors.infoLight 
      : status === 'in-progress' 
        ? theme.colors.warningLight 
        : status === 'completed' 
          ? theme.colors.successLight 
          : theme.colors.dangerLight};
  color: ${({ status, theme }) => 
    status === 'upcoming' 
      ? theme.colors.info 
      : status === 'in-progress' 
        ? theme.colors.warning 
        : status === 'completed' 
          ? theme.colors.success 
          : theme.colors.danger};
`;

// Format date/time for display
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const WorkshopDetails = ({ workshop, onEdit, onDelete, onClose }) => {
  return (
    <DetailsContainer>
      <DetailsHeader>
        <div>
          <Title>{workshop.title}</Title>
          <SubTitle>
            {formatDateTime(workshop.startDateTime)} to {formatDateTime(workshop.endDateTime)}
          </SubTitle>
          <StatusBadge status={workshop.status}>
            {workshop.status === 'upcoming'
              ? 'Upcoming'
              : workshop.status === 'in-progress'
                ? 'In Progress'
                : workshop.status === 'completed'
                  ? 'Completed'
                  : 'Cancelled'}
          </StatusBadge>
        </div>
        <ActionButtons>
          <Button variant="secondary" onClick={onEdit}>
            Edit
          </Button>
          <Button variant="danger" onClick={onDelete}>
            Delete
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </ActionButtons>
      </DetailsHeader>
      
      <DetailSection>
        <DetailItem>
          <Label>Location</Label>
          <Value>{workshop.location}</Value>
        </DetailItem>
        
        {workshop.meetingLink && (
          <DetailItem>
            <Label>Meeting Link</Label>
            <Value>
              <a href={workshop.meetingLink} target="_blank" rel="noopener noreferrer">
                {workshop.meetingLink}
              </a>
            </Value>
          </DetailItem>
        )}
        
        <DetailItem>
          <Label>Registration</Label>
          <Value>
            {workshop.currentParticipants || 0} registered out of {workshop.maxParticipants || 'unlimited'} spots
            <br />
            Registration deadline: {formatDateTime(workshop.registrationDeadline)}
          </Value>
        </DetailItem>
      </DetailSection>
      
      <DetailSection>
        <SectionTitle>Description</SectionTitle>
        <Value>{workshop.longDescription || workshop.shortDescription}</Value>
      </DetailSection>
      
      {workshop.speakers && workshop.speakers.length > 0 && (
        <DetailSection>
          <SectionTitle>Speakers</SectionTitle>
          {workshop.speakers.map((speaker, index) => (
            <SpeakerCard key={index}>
              {speaker.image && (
                <SpeakerImage>
                  <img src={speaker.image} alt={speaker.name} />
                </SpeakerImage>
              )}
              <SpeakerInfo>
                <SpeakerName>{speaker.name}</SpeakerName>
                {speaker.title && <SpeakerTitle>{speaker.title}</SpeakerTitle>}
                {speaker.bio && <Value>{speaker.bio}</Value>}
              </SpeakerInfo>
            </SpeakerCard>
          ))}
        </DetailSection>
      )}
      
      {workshop.agenda && workshop.agenda.length > 0 && (
        <DetailSection>
          <SectionTitle>Agenda</SectionTitle>
          {workshop.agenda.map((item, index) => (
            <AgendaItem key={index}>
              <AgendaTime>{item.time}</AgendaTime>
              <AgendaActivity>{item.activity}</AgendaActivity>
            </AgendaItem>
          ))}
        </DetailSection>
      )}
      
      {workshop.materials && workshop.materials.length > 0 && (
        <DetailSection>
          <SectionTitle>Workshop Materials</SectionTitle>
          {workshop.materials.map((material, index) => (
            <MaterialLink key={index} href={material.link} target="_blank" rel="noopener noreferrer">
              📄 {material.name}
            </MaterialLink>
          ))}
        </DetailSection>
      )}
      
      {workshop.tags && workshop.tags.length > 0 && (
        <DetailSection>
          <SectionTitle>Tags</SectionTitle>
          <TagContainer>
            {workshop.tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </TagContainer>
        </DetailSection>
      )}
    </DetailsContainer>
  );
};

export default WorkshopDetails;